import React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import underline23Mobile from 'stile-shared/static/underline-23-mobile.svg';
import underline23 from 'stile-shared/static/underline-23.svg';
import styled from 'styled-components';

import iconPage from 'stile-shared/assets/icons/icon_page.svg';
import colorWater from 'stile-shared/assets/videos/color_water.mp4';
import crayon from 'stile-shared/assets/videos/crayon.mp4';
import labKits from 'stile-shared/assets/videos/lab_kits.mp4';
import lilies from 'stile-shared/assets/videos/lilies.mp4';
import slime from 'stile-shared/assets/videos/slime.mp4';
import steel from 'stile-shared/assets/videos/steel.mp4';
import waterMill from 'stile-shared/assets/videos/water_mill.mp4';
import weights from 'stile-shared/assets/videos/weights.mp4';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import { Card } from 'stile-shared/src/components/2024/common/Card';
import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { Icon } from 'stile-shared/src/components/2024/common/Icon';
import TitleMenu from 'stile-shared/src/components/2024/common/Included/WITBTitleMenu/WITBTitleMenu';
import Top from 'stile-shared/src/components/2024/common/Included/WITBTopSection/WITBTopSection';
import { StyledWITBTopSection } from 'stile-shared/src/components/2024/common/Included/WITBTopSection/WITBTopSection.styles';
import { VideoPlayer } from 'stile-shared/src/components/2024/common/VideoPlayer';
import { EvidenceSection } from 'stile-shared/src/components/2024/layout/CardSection';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { Section } from 'stile-shared/src/components/2024/layout/Section';
import VideoGallery from 'stile-shared/src/components/2024/layout/VideoGallery';

import { PageLayout } from 'templates/2024/PageLayout';

import GuaranteeBadge from 'components/our-principles/GuaranteeBadge';

const HomePageLayout = styled(PageLayout)``;

const TitleMenuContainer = styled.div`
  display: flex !important;
  flex-direction: column !important;
  padding-bottom: 80px !important;
  p {
    max-width: 563px;
  }
  h1 {
    max-width: 1100px;
    align-self: center;
    @media (max-width: 1700px) {
      max-width: 917px;
    }
    @media (max-width: 1024px) {
      max-width: 802px;
    }
  }

  .content {
    padding-top: 0px !important;
    @media (max-width: 768px) {
      .grid-layout {
        margin-top: 28px !important;
      }
      padding-top: 0px !important;
    }
  }
  .video {
    overflow: visible !important;
    .skeleton.skeleton-video,
    video {
      border-radius: 32px;
    }
    aspect-ratio: 1160/653;
    border-radius: 32px;
    box-shadow: 0px 43px 26px 0px rgba(0, 0, 0, 0.04), 0px 19px 19px 0px rgba(0, 0, 0, 0.06),
      0px 5px 11px 0px rgba(0, 0, 0, 0.07);
    margin: 80px auto 70px;
    @media (max-width: 768px) {
      .skeleton.skeleton-video,
      video {
        border-radius: 16px;
      }
      padding-top: 0px !important;
      border-radius: 16px !important;
      box-shadow: 0px 12.603px 7.621px 0px rgba(0, 0, 0, 0.04),
        0px 5.569px 5.569px 0px rgba(0, 0, 0, 0.06), 0px 1.466px 3.224px 0px rgba(0, 0, 0, 0.07);
      margin: 40px auto 32px;
    }
  }
  @media (max-width: 768px) {
    padding: 0 25px 20px !important;
    margin: 0 !important;
    > div > div {
      padding-top: 17px !important;
    }
  }
`;

const RealClassroomsSection = styled<typeof Section>(Section)`
  .content {
    padding-top: 80px !important;
    @media (max-width: 768px) {
      padding: 0 0 60px !important;
    }
  }
  &.last-section, &.color-coded {
    padding-bottom: 150px;
    @media (max-width: 768px) {
      padding-bottom: 0px;
    }
  }
  .--jumbo {
    margin-top: 70px !important;
    @media (max-width: 768px) {
      margin: 0 0 8px !important;
      text-align: left !important;
    }
  }
  a {
    margin-top: 32px;
    @media (max-width: 768px) {
      margin-top: 30px;
    }
  }
  .underline-23 {
    display: inline-block;
    position: relative;
    z-index: 1;
    :before {
      bottom: 5px;
      content: '';
      display: block;
      position: absolute;
      background-repeat: no-repeat;
      background-position: top left;
      background-size: cover;
      left: -12px;
      width: 116px;
      height: 10px;
      z-index: -1;
      background-image: url('${underline23}');
      @media (max-width: 768px) {
        background-image: url('${underline23Mobile}');
        bottom: -1px;
        left: -8px;
        width: 75px;
        height: 8px;
      }
    }
  }
  .--subheader {
    grid-column: 4 / span 6;
    text-align: center;
    margin-bottom: 80px;
    @media (max-width: 768px) {
      grid-column: 1 / span 12;
      margin-bottom: 40px;
    }
  }
  .grid-layout {
    margin-top: 0 !important;
  }
  @media (max-width: 768px) {
    &.first-section, &.last-section {
      .content {
        padding-top: 60px !important;
      }
    }
    .color-coded {
      .content {
      padding-top: 0 !important;
      }
    }
  }
  .left,
  .secondary-left,
  .tertiary-right {
    display: flex;
    flex-direction: column;
    justify-content: center;
    h2 {
      text-align: left !important;
      margin: 0;
      @media (max-width: 768px) {
        margin-bottom: 20px !important;
      }
    }
  }
  .left {
    grid-column: 1 / span 4;
    max-width: 347px;
  }
  .secondary-left {
    .guarantee {
      color: var(--stile-green-3);
      margin-bottom: 20px;
      gap: 3px;
      display: flex;
      align-items: center;
      @media (max-width: 768px) {
        margin-bottom: 15px
      }
    }
    grid-column: 1 / span 5;
  }
  .tertiary-left {
    grid-column: 1 / span 6;
    .gatsby-image-wrapper {
      border-radius: 32px;
      box-shadow: 0px 43px 26px 0px rgba(0, 0, 0, 0.04), 0px 19px 19px 0px rgba(0, 0, 0, 0.06), 0px 5px 11px 0px rgba(0, 0, 0, 0.07);
    }
  }
  .right {
    grid-column: 5 / span 8;
  }
  .secondary-right {
    .gatsby-image-wrapper {
      border-radius: 32px;
    }
    grid-column: 7 / span 6;
  }
  .tertiary-right {
    grid-column: 8 / span 5;
  }
  @media (max-width: 768px) {
    .left,
    .right,
    .secondary-left,
    .secondary-right,
    .tertiary-left,
    .tertiary-right {
      grid-column: 1 / span 12;
    }
    &.first-section {
      .secondary-left {
        grid-row: 2;
      }
      .secondary-right {
        grid-row: 1;
        margin-bottom: 30px;
      }
      padding-bottom: 0px;
    }
    .tertiary-left { 
      margin-bottom: 30px;
    }
    .left {
      max-width: none;
    }
    .right {
      margin-bottom: 40px;
      @media (max-width: 768px) {
        margin-top: 40px;
      } 
    }
  }
  .guidance, .guarantee-section {
    grid-column: span 12;
  }
  .guarantee-section{
    a {
      @media (max-width: 768px) {
        margin: 28px 0 32px;
      }
    }
  }
  .cards-container {
    grid-column: span 12;
    padding-bottom: 80px;

    @media (max-width: 768px) {
      padding: 60px 0 0;
    }

    .card {
      color: var(--stile-grey-8);
      margin-top: 80px;
      .header {
        padding-bottom: 25px;
      }
      padding: 35px 19px;
      &:last-child {
        margin-bottom: 0;
      }
      @media (max-width: 768px) {
        margin-top: 0px;
    }
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  position: relative;
  padding: 65px 0 54px;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  overflow: hidden;
  background: var(--stile-grey-8);
  color: var(--white);
  p {
    color: var(--white);
    max-width: 731px;
  }
  a {
    margin-bottom: 80px;
  }
  h2 {
    width: 100%;
    margin-bottom: 20px;
    text-align: left;
  }
  @media (max-width: 768px) {
    padding: 0;
  }
`;
const ImageBlock = styled<typeof VideoGallery>(VideoGallery)`
  grid-template-columns: 15fr 13fr 36fr 13fr 15fr;

  @media (max-width: 768px) {
    padding: 60px 0 60px;
    gap: 12px;
    grid-template-columns: 15fr 177fr 12fr 196fr 12fr 177fr 15fr;
    grid-template-rows: 100fr 12fr 85fr 12fr 100fr;
    .first {
      grid-row: 1 / span 1 !important;
      grid-column: 2 / span 2 !important;
    }
    .third {
      grid-row: 2 / span 2 !important;
    }
    .hero {
      grid-row: 2 / span 3 !important;
    }
    .fifth {
      grid-row: 3 / span 2 !important;
    }
    .sixth {
      grid-row: 5 / span 1 !important;
    }
    .last {
      grid-row: 5 / span 1 !important;
    }
`;
const Header = styled<typeof StyledWITBTopSection>(StyledWITBTopSection)`
  z-index: 1;
  will-change: transform;
  transition: transform 0.2s ease-out;
  position: relative;
  max-width: 1160px;
  padding-top: 35px;
  margin-bottom: 80px;
  h2 {
    text-align: center !important;
  }
  @media (max-width: 768px) {
    text-align: left !important;
    transform: none !important;
    position: static;
    padding: 0 25px;
    margin-bottom: 0;
  }
`;

const WhatsIncludedSection = styled<typeof EvidenceSection>(EvidenceSection)`
  .content {
    padding-bottom: 50px !important;
    @media (max-width: 768px) {
      padding-bottom: 0 !important;
    }
  }
  .--jumbo {
    margin: 40px 0 0 !important;
    @media (max-width: 768px) {
      margin: 0px !important;
    }
  }
  .whats-included {
    grid-column: 1 / span 12;
    display: flex;
  }
  .gatsby-image-wrapper {
    border-radius: 32px;
    box-shadow: 0px 43px 26px 0px rgba(0, 0, 0, 0.04), 0px 19px 19px 0px rgba(0, 0, 0, 0.06),
      0px 5px 11px 0px rgba(0, 0, 0, 0.07);
  }
  .small-left,
  .small-right {
    display: flex;
    flex-direction: column;
    h2 {
      text-align: left !important;
    }
    div {
      padding-left: 20px;
      @media (max-width: 768px) {
        padding-left: 0px;
      }
    }
    text-align: left;
    gap: 70px;
    padding-bottom: 150px !important;
    @media (max-width: 768px) {
      gap: 30px;
      padding-bottom: 20px !important;
    }
  }
  .big-left,
  .big-right {
    display: flex;
    align-items: end;
    padding-bottom: 150px !important;
    @media (max-width: 768px) {
      padding-bottom: 60px !important;
    }
  }
  .small-left {
    grid-column: 2 / span 4;
  }
  .small-right {
    grid-column: 8 / span 4;
  }
  .big-left {
    grid-column: 2 / span 6;
  }
  .big-right {
    grid-column: 6 / span 6;
  }
  @media (max-width: 768px) {
    .small-left,
    .small-right,
    .big-left,
    .big-right {
      grid-column: 1 / span 12;
    }
    .small-left,
    .small-right {
      flex-direction: column-reverse;
    }
  }
`;

function LabKitsPage(props: PageProps) {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();

    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const exploreCards = [
    {
      title: 'Stile X Workbooks',
      description:
        'Designed for hands-on learning and perfectly matched to the digital curriculum.',
      url: '/what-is-stile/stile-x/',
    },
    {
      title: 'Inside a Stile Classroom',
      description: 'Helping teachers implement evidence-based strategies.',
      url: '/what-is-stile/stile-classroom',
    },
    {
      title: 'Professional Learning',
      description:
        'Master Stile at your own pace with evidence-based pedagogies and personalised support.',
      url: '/what-is-stile/stile-pl/',
    },
  ];
  const exploreCurriculumImage = (
    <StaticImage
      src="../../components/our-principles/images/teachersaying_quality_4.png"
      alt="Teacher Saying Quality"
      placeholder="blurred"
      layout="constrained"
      width={124}
      height={124}
    />
  );
  return (
    <HomePageLayout {...props}>
      <TitleMenuContainer className="content">
        <TitleMenu>
          <Top
            title="Lab kits for inquiring minds"
            description="When learning with Stile, students spend about half their time doing hands-on activities, engineering challenges, and, of course, labs. For these, Stile’s lab kits provide everything needed to run all 71 of them."
          />
          <VideoPlayer url={labKits} />
        </TitleMenu>
      </TitleMenuContainer>
      <RealClassroomsSection backgroundColor="--stile-grey-8" textColor="--white">
        <Grid className="cards-container">
          <div className="left">
            <h2>Made for real classrooms</h2>
            <p>
              As with everything at Stile, we're relentlessly committed to quality. One of our
              principles is to{' '}
              <span style={{ color: 'var(--stile-green-3)', fontWeight: '600' }}>
                exceed teacher expectations
              </span>
              , so there was no way that we were going to make a big plastic bin full of jumbled
              materials. <br /> <br />
              Stile’s kits are organized, durable, and delightful to use.
            </p>
          </div>
          <div className="right">
            <StaticImage
              src="../../../../stile-shared/assets/images/lab-kits/real-classrooms.jpg"
              alt="Workshops & events"
            />
          </div>
          <Card
            backgroundColor="--white"
            cardTitle={<span>General Equipment</span>}
            headerImage={
              <StaticImage
                src="../../../../stile-shared/assets/images/lab-kits/general.jpg"
                alt="Workshops & events"
              />
            }
          />

          <Card
            backgroundColor="--white"
            cardTitle={
              <span>
                Glassware
                {!isMobile && <br />} Kit
              </span>
            }
            headerImage={
              <StaticImage
                src="../../../../stile-shared/assets/images/lab-kits/glassware.jpg"
                alt="Workshops & events"
              />
            }
          />

          <Card
            backgroundColor="--white"
            cardTitle={<span>Unit{!isMobile && <br />} Materials</span>}
            headerImage={
              <StaticImage
                src="../../../../stile-shared/assets/images/lab-kits/unit-material.jpg"
                alt="Workshops & events"
              />
            }
          />
        </Grid>
      </RealClassroomsSection>
      <WhatsIncludedSection
        headingColor="--stile-grey-8"
        headingContent={<h1 className="--jumbo">What’s in the box?</h1>}
        textColor="--stile-grey-8"
      >
        <div className="--subheader">
          Some classrooms are already filled with science equipment. Others have none at all. That’s
          why you can mix and match Stile’s kits so that you’re only purchasing what you need.
        </div>
        <div className="small-left">
          <StaticImage
            src="../../../../stile-shared/assets/images/lab-kits/reusable-resources.jpg"
            alt="Reusable resources"
          />
          <div>
            <h2>Reusable resources</h2>
            <p>
              General Equipment includes everything from measuring devices to lab utensils,
              supporting all units of study for your grade. Durable and reusable, these materials
              will be the backbone of your scientific explorations!
            </p>
          </div>
        </div>
        <div className="big-right">
          <StaticImage
            src="../../../../stile-shared/assets/images/lab-kits/general-equipment-box.jpg"
            alt="General Equipment"
          />
        </div>
        {!isMobile && (
          <div className="big-left">
            <StaticImage
              src="../../../../stile-shared/assets/images/lab-kits/unit-materials-box.jpg"
              alt="Unit Materials"
            />
          </div>
        )}
        <div className="small-right">
          <StaticImage
            src="../../../../stile-shared/assets/images/lab-kits/specific-supplies.jpg"
            alt="Specific supplies"
          />
          <div>
            <h2>Specific supplies</h2>
            <p>
              Unit Materials contain all the specific materials you need to run the labs in each
              grade’s unit. The materials include consumables and non-consumables, such as magnets,
              craft supplies, batteries, and other specialized materials.
            </p>
          </div>
        </div>
        {isMobile && (
          <div className="big-left">
            <StaticImage
              src="../../../../stile-shared/assets/images/lab-kits/unit-materials-box.jpg"
              alt="Unit Materials"
            />
          </div>
        )}
        <div className="small-left">
          <StaticImage
            src="../../../../stile-shared/assets/images/lab-kits/safe-and-snug.jpg"
            alt="Safe and snug"
          />
          <div>
            <h2>Safe and snug</h2>
            <p>
              Glassware comes hand-packed and cradled in custom foam inserts to ensure secure
              breakage-free shipping and storage.
            </p>
          </div>
        </div>
        <div className="big-right">
          <StaticImage
            src="../../../../stile-shared/assets/images/lab-kits/glassware-box.jpg"
            alt="Glassware"
          />
        </div>
      </WhatsIncludedSection>
      <RealClassroomsSection
        backgroundColor="--stile-grey-8"
        headingColor="--white"
        headingContent={<h1 className="--jumbo">Crystal clear guidance</h1>}
        textColor="--white"
        className="first-section"
      >
        <Grid className="guidance">
          <div className="secondary-left">
            <h2>Step-by-step instructions</h2>
            <p>
              We provide step-by-step instructions for teachers to use Stile’s labs, minimizing prep
              time and making them more enjoyable to run.
            </p>
          </div>
          <div className="secondary-right">
            <StaticImage
              src="../../../../stile-shared/assets/images/lab-kits/step-by-step.jpg"
              alt="Workshops & events"
            />
          </div>
        </Grid>
      </RealClassroomsSection>
      <ContentWrapper>
        <Header>
          <h2>Demo videos for every lab</h2>
          <p>
            Not only have we tested every lab, but we’ve filmed ourselves doing them too. Our studio
            team have then edited the footage and added visual overlays to create short sharp
            annotated demo videos for teachers to either use themselves or play at the front of the
            classroom.
          </p>
        </Header>
        <ImageBlock
          videoAspectRatio="1160/653"
          items={[
            { type: 'video', url: colorWater },
            { type: 'video', url: weights },
            { type: 'video', url: steel },
            { type: 'video', url: waterMill },
            { type: 'video', url: crayon },
            { type: 'video', url: slime },
            { type: 'video', url: lilies },
          ]}
        />
      </ContentWrapper>
      <RealClassroomsSection
        backgroundColor="--stile-grey-8"
        textColor="--white"
        className="color-coded"
      >
        <Grid className="guidance">
          <div className="secondary-left">
            <h2>Color-coded compartments</h2>
            <p>
              No more guessing what item belongs to what lab. We’ve color-coded each lab and
              compartmentalised their materials so you know exactly what’s required each time.
            </p>
          </div>
          <div className="secondary-right">
            <StaticImage
              src="../../../../stile-shared/assets/images/lab-kits/color-coded.png"
              alt="Workshops & events"
            />
          </div>
        </Grid>
      </RealClassroomsSection>
      <RealClassroomsSection className="last-section">
        <Grid className="guarantee-section">
          <div className="secondary-left">
            <div className="guarantee">
              <GuaranteeBadge />
              <h6 className="h7">Guarantee</h6>
            </div>
            <h2>
              Our labs <span className="underline-23">just</span> work
            </h2>
            <p>
              We take pride in our “Just Works” Guarantee: We’ll send you a
              <strong>
                {' '}
                $500 Vernier voucher if one of Stile’s labs doesn’t work as promised.
              </strong>{' '}
              No longer will you find yourself saying “this usually works”, or “that’s just science,
              things often don’t work!”.
            </p>
            <Button
              label="Our guarantees & principles"
              newTab={false}
              href="/who-we-are/our-principles/"
            />
          </div>
          <div className="secondary-right">
            <StaticImage
              src="../../../../stile-shared/assets/images/homepage/robust_labs.png"
              alt="Workshops & events"
            />
          </div>
        </Grid>
      </RealClassroomsSection>
      <RealClassroomsSection backgroundColor="--stile-green-2" className="last-section">
        <Grid className="guidance">
          <div className="tertiary-left">
            <StaticImage
              src="../../../../stile-shared/assets/images/lab-kits/sample-lab-kits.png"
              alt="Workshops & events"
            />
          </div>
          <div className="tertiary-right">
            <h2>Download a sample lab guide</h2>
            <p>
              Do a deeper dive on Stile's labs and download a sample Lab Guide for Stile's middle
              school unit on Energy Conservation.
            </p>
            <Button
              label={
                <>
                  <Icon src={iconPage} iconSize="20px" style={{ marginRight: 6 }} />
                  <span>Download sample lab guide</span>
                </>
              }
              newTab={false}
              style="white"
              href="/resources/lab-guide/"
            />
          </div>
        </Grid>
      </RealClassroomsSection>
      <ExploreCurriculum
        title="Explore our core science curriculum"
        exploreCardsData={exploreCards}
        image={exploreCurriculumImage}
      />
    </HomePageLayout>
  );
}

export default LabKitsPage;
